import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Download, FileText, Loader2, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { compressPdf, PdfCompressLevel } from "@/Api/pdfTools";
import { DropZone } from "./DropZone";
import { downloadBlob, formatBytes } from "./utils";

const MAX_SIZE = 60 * 1024 * 1024;

const LEVELS: Array<{ value: PdfCompressLevel; title: string; description: string }> = [
  {
    value: "extreme",
    title: "Compressão extrema",
    description: "Menor tamanho, qualidade menor",
  },
  {
    value: "recommended",
    title: "Compressão recomendada",
    description: "Boa qualidade, boa compressão",
  },
  {
    value: "low",
    title: "Compressão baixa",
    description: "Alta qualidade, menos compressão",
  },
];

type Result = { blob: Blob; original: number; compressed: number; fileName: string };

export const CompressPdf = () => {
  const [file, setFile] = useState<File | null>(null);
  const [level, setLevel] = useState<PdfCompressLevel>("recommended");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  const reset = () => {
    setFile(null);
    setResult(null);
  };

  const handleCompress = async () => {
    if (!file) return;

    if (file.size > MAX_SIZE) {
      toast.error("O PDF deve ter no máximo 60 MB.");
      return;
    }

    setLoading(true);
    try {
      const response = await compressPdf(file, level);

      if (response) {
        setResult({
          blob: response.blob,
          original: response.originalSize ?? file.size,
          compressed: response.compressedSize ?? response.blob.size,
          fileName: `${file.name.replace(/\.pdf$/i, "")}-comprimido.pdf`,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const saved = Math.max(0, Math.round((1 - result.compressed / result.original) * 100));
    const reduced = result.compressed < result.original;

    return (
      <div className="flex flex-col items-center gap-5 rounded-xl border bg-muted/20 px-6 py-12 text-center">
        <CheckCircle2 className="h-12 w-12 text-green-600" />
        <div className="space-y-1">
          <h2 className="text-2xl font-bold">
            {reduced ? "Seu PDF foi comprimido" : "Este PDF já está otimizado"}
          </h2>
          <p className="text-muted-foreground">
            {reduced
              ? `O arquivo ficou ${saved}% menor.`
              : "Não foi possível reduzir mais o tamanho sem perder qualidade."}
          </p>
        </div>

        <div className="flex items-center gap-3 text-lg">
          <span className="text-muted-foreground line-through">{formatBytes(result.original)}</span>
          <span className="font-bold">{formatBytes(result.compressed)}</span>
          {reduced && (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-sm font-semibold text-green-700">
              -{saved}%
            </span>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          <Button
            size="lg"
            className={cn("h-12 px-8", colors.bg_primary)}
            onClick={() => downloadBlob(result.blob, result.fileName)}
          >
            <Download className="mr-2 h-5 w-5" />
            Baixar PDF comprimido
          </Button>
          <Button size="lg" variant="outline" className="h-12" onClick={reset}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Comprimir outro PDF
          </Button>
        </div>
      </div>
    );
  }

  if (!file) {
    return (
      <DropZone
        title="Comprimir arquivos PDF"
        description="Diminua o tamanho do seu arquivo PDF, mantendo a melhor qualidade possível."
        buttonLabel="Selecionar arquivo PDF"
        hint="ou arraste e solte o PDF aqui (até 60 MB)"
        accept="application/pdf,.pdf"
        isValid={(f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")}
        invalidMessage="Selecione um arquivo PDF."
        onInvalid={(message) => toast.error(message)}
        onFiles={([selected]) => setFile(selected)}
      />
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-4 rounded-xl border bg-muted/20 p-6">
        <div className="flex items-center gap-4 rounded-lg border bg-background p-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600">
            <FileText className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{file.name}</p>
            <p className="text-sm text-muted-foreground">{formatBytes(file.size)}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={reset} disabled={loading} title="Remover arquivo">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          O texto continua selecionável: só as imagens e fontes do PDF são otimizadas.
        </p>
      </div>

      <div className="space-y-4 rounded-xl border p-6">
        <h3 className="text-lg font-semibold">Nível de compressão</h3>
        <RadioGroup value={level} onValueChange={(v) => setLevel(v as PdfCompressLevel)}>
          {LEVELS.map((item) => (
            <Label
              key={item.value}
              htmlFor={`level-${item.value}`}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors",
                level === item.value && "border-primary bg-primary/5"
              )}
            >
              <RadioGroupItem id={`level-${item.value}`} value={item.value} className="mt-1" />
              <span>
                <span className="block font-semibold">{item.title}</span>
                <span className="block text-sm font-normal text-muted-foreground">
                  {item.description}
                </span>
              </span>
            </Label>
          ))}
        </RadioGroup>

        <Button
          size="lg"
          className={cn("h-12 w-full", colors.bg_primary)}
          onClick={handleCompress}
          disabled={loading}
        >
          {loading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
          {loading ? "Comprimindo..." : "Comprimir PDF"}
        </Button>
      </div>
    </div>
  );
};
